import axios from "axios";

// Set per-environment in .env.development / .env.uat / .env.production
const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://uat.feezaap.com/api/";
// Extract the base domain for images (e.g., https://uat.feezaap.com)
export const IMAGE_BASE_URL = REACT_APP_API_BASE_URL.replace(/\/api\/?$/, "");

const apiClient = axios.create({
    baseURL: REACT_APP_API_BASE_URL,
    timeout: 15000,
    headers: {
        "Content-Type": "application/json",
    },
});
console.log("API BASE URL:", REACT_APP_API_BASE_URL);

let refreshPromise = null;

const getRequestBodyText = (data) => {
    if (!data) return "";
    if (typeof data === "string") return data;
    if (typeof data?.toString === "function") return data.toString();
    return "";
};


const clearAuthSession = () => {
    console.warn("🚪 [Auth] Clearing authentication session...");

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("lastRefreshTime"); // optional cleanup

    console.warn("✅ [Auth] Session cleared");
};

export const initializeTokenRefresh = () => {
    console.log("🚀 [Auth] Token refresh initialized (401-based only)");
};

const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    console.group("🔄 [Refresh Token API]");

    if (!refreshToken) {
        console.error("❌ No refresh token available");
        console.groupEnd();
        throw new Error("No refresh token available");
    }

    const params = new URLSearchParams();
    params.append("grant_type", "refresh_token");
    params.append("refresh_token", refreshToken);

    console.log("📤 Calling:", `${REACT_APP_API_BASE_URL}token`);
    console.log("📤 Content-Type: application/x-www-form-urlencoded");
    console.log("📤 Body:", params.toString());

    try {
        const response = await axios.post(
            `${REACT_APP_API_BASE_URL}token`,
            params.toString(),
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                timeout: 15000,
            }
        );

        const data = response.data;

        if (!data?.access_token) {
            throw new Error("No access token received");
        }

        // Save new access token
        localStorage.setItem("token", data.access_token);
        console.log("💾 New access token saved");

        // Save new refresh token if provided
        if (data.refresh_token) {
            localStorage.setItem("refreshToken", data.refresh_token);
            console.log("💾 New refresh token saved");
        }

        console.log("✅ Token refresh completed successfully");
        console.groupEnd();

        return data.access_token;
    } catch (error) {
        console.error("❌ Refresh token API failed");
        console.error("Status:", error?.response?.status);
        console.error("Data  :", error?.response?.data || error.message);
        console.groupEnd();
        throw error;
    }
};

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        const requestUrl = config?.url || "";

        if (token && !requestUrl.includes("token")) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            };

            console.log(`📤 [API Request] ${config.method?.toUpperCase()} ${requestUrl}`);
            console.log("🔐 Authorization header attached");
        }

        return config;
    },
    (error) => {
        console.error("❌ [Request Interceptor Error]", error);
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => {
        console.log(
            `📥 [API Response] ${response.status} ${response.config?.url}`
        );
        return response;
    },
    async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config;
        const requestUrl = originalRequest?.url || "";
        const requestBody = getRequestBodyText(originalRequest?.data);
        const isTokenRequest = requestUrl.includes("token");
        const isRefreshTokenRequest = isTokenRequest && requestBody.includes("grant_type=refresh_token");

        console.group("🚨 [Response Interceptor]");
        console.log("URL    :", requestUrl);
        console.log("Status :", status);
        console.log("Retry? :", !!originalRequest?._retry);

        // ----------------------------------------
        // If refresh token API itself fails
        // ----------------------------------------
        if (isRefreshTokenRequest) {
            console.warn("⚠️ Refresh token API failed");
            console.warn("🚪 Clearing session and redirecting to login...");

            clearAuthSession();
            console.groupEnd();

            // Redirect to login page
            window.location.replace("/");

            return Promise.reject(new Error("AUTH_SESSION_EXPIRED"));
        }

        if (isTokenRequest) {
            let message = "Username or password is incorrect";
            if (error?.response?.data) {
                if (typeof error.response.data === "string") {
                    message = error.response.data;
                } else {
                    message =
                        error.response.data.message ||
                        error.response.data.Message ||
                        error.response.data.error_description ||
                        error.response.data.error ||
                        error.response.data.Error ||
                        message;
                }
            } else if (error?.message) {
                message = error.message;
            }

            console.error("âŒ Login request failed:", message);
            console.groupEnd();

            return Promise.reject(message);
        }

        // ----------------------------------------
        // First 401 -> Refresh token and retry once
        // ----------------------------------------
        if (
            status === 401 &&
            originalRequest &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            try {
                console.log("🔄 401 detected. Calling refresh token API...");

                // Prevent duplicate refresh calls
                refreshPromise = refreshPromise || refreshAccessToken();
                const newToken = await refreshPromise;

                console.log("✅ Refresh token API succeeded");

                // Update Authorization header
                originalRequest.headers = {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${newToken}`,
                };

                console.log("🔁 Retrying original request...");
                console.groupEnd();

                // Retry original request once
                return apiClient(originalRequest);
            } catch (refreshError) {
                console.error("❌ Refresh token API failed");
                console.error(
                    "Status:",
                    refreshError?.response?.status
                );

                clearAuthSession();
                console.groupEnd();

                // Redirect to login page
                window.location.replace("/");

                return Promise.reject(new Error("AUTH_SESSION_EXPIRED"));
            } finally {
                refreshPromise = null;
            }
        }

        // ----------------------------------------
        // Second 401 after retry
        // (_retry is already true)
        // ----------------------------------------
        if (
            status === 401 &&
            originalRequest &&
            originalRequest._retry
        ) {
            console.error("❌ Retried request also returned 401");
            console.warn("🚪 Session expired. Redirecting to login...");

            clearAuthSession();
            console.groupEnd();

            // Redirect to login page
            window.location.replace("/");

            return Promise.reject(new Error("AUTH_SESSION_EXPIRED"));
        }

        // ----------------------------------------
        // Other errors
        // ----------------------------------------
        let message = "Something went wrong";
        if (error?.response?.data) {
            if (typeof error.response.data === "string") {
                message = error.response.data;
            } else {
                message =
                    error.response.data.message ||
                    error.response.data.Message ||
                    error.response.data.error ||
                    error.response.data.Error ||
                    error.message ||
                    "Something went wrong";
            }
        } else {
            message = error.message || "Something went wrong";
        }

        console.error("❌ Request failed:", message);
        console.groupEnd();

        return Promise.reject(message);
    }
);

export default apiClient;
